/**
 * stl-fit-guide.js - the Say The Label fit guide (28 Oct 2026). Markup and words:
 * sections/stl-fit-guide.liquid. Sizes: assets/stl-fit-engine.js. Products:
 * sections/stl-fit-data.liquid.
 *
 * Before the box is first opened this file does two small things and nothing else: shows the tab
 * once THIS page has been open for the setting's seconds (default 5, counted from the page's own
 * load, never across pages; F92, 29 Oct 2026), and keeps it bottom right beside the Questions?
 * pill without touching it or the $15 tab. The section only renders on product pages with a Size
 * option, so on every other page this file is never requested.
 * The engine and the product data are only requested when the box opens.
 */
import { storage, overlays, openOverlay, closeOverlay, onReady, config, money } from '@stl/core';

const ON = 'stl-is-on';
const KEY_OFF = 'stl_fit_off';
const KEY_ME = 'stl_fit_me';
const KEY_UNIT = 'stl_fit_unit';
const KEY_DATA = 'stl_fit_data';
const MODAL = 'stl-fitModal';
const CATS = ['top', 'bottom', 'jacket', 'dress'];

const $ = (sel, root) => (root || document).querySelector(sel);
const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));

/** Fills [token] placeholders in a setting. */
function fill(text, tokens) {
  return String(text || '').replace(/\[(\w+)\]/g, (whole, name) => (tokens && tokens[name] !== undefined ? String(tokens[name]) : whole));
}

/** The fit guide's own words: the JSON right after #stl-fitModal in this section, never the
 *  product page size block's (that one is [data-stl-pd-fit-words], F88 repair 29 Oct 2026). */
function words() {
  try {
    const node = $('#' + MODAL + ' ~ script[data-stl-fit-words]') || $('[data-stl-fit-words]', document.getElementById(MODAL));
    return JSON.parse((node || {}).textContent || '{}');
  } catch (e) {
    return {};
  }
}

function el(tag, cls, text) {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (text !== undefined && text !== null) node.textContent = text;
  return node;
}

/* ------------------------------------------------------------------ units (display only) */

const CM_PER_IN = 2.54;

function shown(cm, unit) {
  if (!Number.isFinite(cm)) return '';
  if (unit === 'in') {
    /* one decimal, the same as the box writes back into a field, so 34.3 never reads as 34.5 */
    return Math.round((cm / CM_PER_IN) * 10) / 10 + 'in';
  }
  /* one decimal when she typed one: 89.5 must not read as 90 beside "between S and M" */
  return Math.round(cm * 10) / 10 + 'cm';
}

function shownHeight(cm, unit) {
  if (!Number.isFinite(cm)) return '';
  if (unit !== 'in') return Math.round(cm) + 'cm';
  const total = Math.round(cm / CM_PER_IN);
  return Math.floor(total / 12) + 'ft ' + (total % 12) + 'in';
}

/**
 * A typed value as a number in the unit she is using. Height in inches also takes 5'7, 5ft 7,
 * and feet.inches the way a phone keypad allows it (5.5 is 5ft 5in, 5.10 is 5ft 10in): the
 * decimal keypad has no apostrophe, and a height of 4 to 7 inches is never meant.
 */
function typedNumber(name, raw, unit) {
  const text = String(raw || '').trim();
  if (!text) return null;
  if (name === 'height' && unit === 'in') {
    const ft = text.match(/^(\d+)\s*(?:ft|'|’|\s)\s*(\d+(?:\.\d+)?)?\s*(?:in|"|”)?$/i);
    if (ft && Number(ft[1]) <= 8) return Number(ft[1]) * 12 + Number(ft[2] || 0);
    const dot = text.match(/^([4-7])[.,](\d{1,2})$/);
    if (dot && Number(dot[2]) <= 11) return Number(dot[1]) * 12 + Number(dot[2]);
  }
  const n = parseFloat(text.replace(',', '.'));
  return Number.isFinite(n) && /^[\d.,\s]+(cm|in|")?$/i.test(text) ? n : NaN;
}

/* ------------------------------------------------------------------ the tab */

const tab = { el: null, open: null, ready: false, off: false, timer: 0, opened: false, at: 0 };

function askDocked() {
  return document.documentElement.hasAttribute('data-stl-ask-docked');
}

function teaserUp() {
  const teaser = document.getElementById('stl-teaser');
  return !!teaser && teaser.classList.contains(ON);
}

/**
 * One way in: this page's clock reaching the setting (F92). Hidden while anything covers the page:
 * the pop-up, a drawer, the menu, search, the cart drawer, any stl modal or theme dialog
 * (overlays.anyOpen covers its own box too), or the Questions? panel docked.
 */
function tabSync() {
  if (!tab.el) return;
  const show = tab.ready && !tab.off && !overlays.isBusy() && !overlays.anyOpen() && !askDocked();
  if (show && !tab.el.classList.contains(ON)) place();
  tab.el.classList.toggle(ON, show);
}

/**
 * Bottom right: the tab's right edge sits 8px to the left of the Questions? pill (the gap it used
 * to keep beside the $15 tab), or in the corner itself where there is no pill. It picks the
 * longest label whose left edge stays 8px clear of the $15 tab while that is up. If even the tape
 * measure alone would touch it (a very narrow phone), the tab sits on top of the pill instead.
 */
function place() {
  const node = tab.el;
  if (!node) return;
  const vw = document.documentElement.clientWidth || window.innerWidth;
  const gut = vw >= 900 ? 24 : 16; /* --stl-gut-dk and --stl-gut, the pill's own offsets (F90) */
  let leftLimit = gut;
  const teaser = document.getElementById('stl-teaser');
  if (teaserUp()) {
    const r = teaser.getBoundingClientRect();
    if (r.width) leftLimit = Math.round(r.right + 8);
  }
  let rightEdge = vw - gut;
  let pill = null;
  const ask = document.getElementById('stl-askPill');
  if (ask && ask.getClientRects().length) {
    const a = ask.getBoundingClientRect();
    if (a.width) {
      pill = a;
      rightEdge = Math.floor(a.left - 8);
    }
  }
  node.style.setProperty('--stl-fit-right', vw - rightEdge + 'px');
  node.style.removeProperty('--stl-fit-bottom');
  /* full line, then tape and "Sizing", then "Sizing" alone, then the tape alone */
  for (const size of ['full', 'short', 'word', 'icon']) {
    node.setAttribute('data-stl-fit-size', size);
    if (rightEdge - node.offsetWidth >= leftLimit) return;
  }
  /* nothing fits beside it: stack on top of the Questions? pill, tape measure only. The pill's
     bottom is its own CSS offset (the lift over the phone Add to bag bar is added by CSS) */
  node.setAttribute('data-stl-fit-size', 'icon');
  if (pill) {
    const root = document.documentElement;
    const lift = root.hasAttribute('data-stl-sticky-on') ? parseFloat(window.getComputedStyle(root).getPropertyValue('--stl-sticky-lift')) || 0 : 0;
    node.style.setProperty('--stl-fit-right', Math.round(vw - pill.right) + 'px');
    node.style.setProperty('--stl-fit-bottom', Math.round(window.innerHeight - pill.top - lift + 6) + 'px');
  }
}

function tabReady() {
  if (tab.ready) return;
  tab.ready = true;
  tab.at = Math.round(window.performance.now());
  tabSync();
}

/** Seconds on THIS page (F92): counted from the page's own navigation start, never carried across pages. */
function startClock(delayMs) {
  const left = Math.max(0, delayMs - window.performance.now());
  tab.timer = window.setTimeout(tabReady, left);
}

/* ------------------------------------------------------------------ data and engine, first open only */

let loading = null;

function marketRoot() {
  let root = '';
  try {
    root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
  } catch (e) {
    root = '';
  }
  if (!root) root = (config().routes && config().routes.root_url) || '/';
  return root.endsWith('/') ? root : root + '/';
}

/**
 * The styles, each with the colourways on a not-for-sale template removed and any colourway's
 * own custom.fit_note attached as notes[handle] (both from sections/stl-fit-data.liquid's
 * data-stl-fit-extra block beside the style).
 */
function parseStyles(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const out = [];
  const extra = {};
  doc.querySelectorAll('script[data-stl-fit-extra]').forEach((s) => {
    try {
      extra[s.getAttribute('data-stl-fit-extra')] = JSON.parse(s.textContent);
    } catch (e) {
      /* a broken extra block only loses its notes */
    }
  });
  doc.querySelectorAll('script[data-stl-style]').forEach((s) => {
    try {
      const style = JSON.parse(s.textContent);
      const x = extra[style.current] || extra[s.getAttribute('data-stl-product')] || {};
      const skip = Array.isArray(x.skip) ? x.skip : [];
      if (skip.length) style.colourways = (style.colourways || []).filter((c) => skip.indexOf(c.handle) === -1);
      style.notes = x.notes && typeof x.notes === 'object' ? x.notes : {};
      out.push(style);
    } catch (e) {
      /* one broken style is skipped, not fatal */
    }
  });
  return out;
}

/* stock and prices move (sell-outs, the Black Friday price change): the cached list is reused for
   ten minutes at most, and never across themes (preview and live in one tab) */
const DATA_TTL_MS = 10 * 60 * 1000;

function themeId() {
  try {
    return String((window.Shopify && window.Shopify.theme && window.Shopify.theme.id) || '');
  } catch (e) {
    return '';
  }
}

function load(modal) {
  if (loading) return loading;
  const root = marketRoot();
  const cacheKey = root + '|' + (config().currency || '') + '|' + themeId();
  const engineUrl = modal.getAttribute('data-stl-fit-engine');
  const cached = storage.get(KEY_DATA, null, 'session');
  const fresh = cached && cached.k === cacheKey && Number(cached.t) > Date.now() - DATA_TTL_MS && Number(cached.t) <= Date.now();
  const data =
    fresh && Array.isArray(cached.styles) && cached.styles.length
      ? Promise.resolve(cached.styles)
      : fetch(root + '?section_id=stl-fit-data', { credentials: 'same-origin' })
          .then((r) => (r.ok ? r.text() : Promise.reject(new Error('HTTP ' + r.status))))
          .then((html) => {
            const styles = parseStyles(html);
            if (!styles.length) throw new Error('no styles');
            storage.set(KEY_DATA, { k: cacheKey, t: Date.now(), styles }, 'session');
            return styles;
          });
  const started = Date.now();
  loading = Promise.all([import(engineUrl), data]).catch((err) => {
    loading = null;
    throw err;
  });
  /* the next open after the time limit fetches again */
  loading.then(() => {
    window.setTimeout(() => {
      loading = null;
    }, Math.max(0, DATA_TTL_MS - (Date.now() - started)));
  }).catch(() => {});
  return loading;
}

/* ------------------------------------------------------------------ the box */

const ui = { modal: null, form: null, w: {}, unit: 'cm', me: null, chip: 'all', cards: [], moreOpen: false };

/**
 * Writes a cm value into a field in the unit showing, to one decimal, and remembers the exact cm
 * behind it: while the text is untouched, readForm uses that cm, so switching units or pressing
 * Edit never moves a value near a band edge into the next band (29 Oct 2026 review, FL-6).
 */
function writeField(input, cm) {
  if (!Number.isFinite(cm) || !cm) {
    input.value = '';
    delete input.dataset.stlCm;
    delete input.dataset.stlShown;
    return;
  }
  const v = ui.unit === 'in' ? cm / CM_PER_IN : cm;
  input.value = String(Math.round(v * 10) / 10);
  input.dataset.stlCm = String(cm);
  input.dataset.stlShown = input.value;
}

/** The exact cm behind a field the box filled in, or null once she has typed over it. */
function keptCm(input) {
  if (input.dataset.stlShown === undefined || input.value !== input.dataset.stlShown) return null;
  const cm = Number(input.dataset.stlCm);
  return Number.isFinite(cm) ? cm : null;
}

function setUnit(unit, convert) {
  const old = ui.unit;
  ui.unit = unit === 'in' ? 'in' : 'cm';
  $$('[data-stl-fit-unit-btn]', ui.modal).forEach((b) => b.setAttribute('aria-pressed', b.getAttribute('data-stl-fit-unit-btn') === ui.unit ? 'true' : 'false'));
  /* the suffix is in each field's description, so a screen reader hears the unit too */
  $$('[data-stl-fit-suffix]', ui.modal).forEach((s) => {
    const short = $('[data-stl-fit-unit-short]', s);
    const long = $('[data-stl-fit-unit-long]', s);
    if (short) short.textContent = ui.unit;
    if (long) long.textContent = ui.unit === 'in' ? ui.w.in_word || 'inches' : ui.w.cm_word || 'cm';
  });
  const hh = $('#stl-fit-height-help', ui.modal);
  if (hh) hh.textContent = (ui.unit === 'in' && ui.w.height_help_in) || ui.w.height_help || hh.textContent;
  if (convert && old !== ui.unit) {
    ['bust', 'waist', 'hip', 'height'].forEach((name) => {
      const input = ui.form.elements[name];
      let cm = keptCm(input);
      if (cm === null) {
        const n = typedNumber(name, input.value, old);
        if (n === null || !Number.isFinite(n)) return;
        cm = old === 'in' ? n * CM_PER_IN : n;
      }
      writeField(input, cm);
    });
  }
  /* only a tap on the switch is remembered. Storing the page's default too meant a US shopper's
     first page on the Australian site saved "cm" a moment before the market redirect took her to
     /en-us, and "cm" then beat the inches default there for good (render, 29 Oct 2026). */
  if (convert) storage.set(KEY_UNIT, ui.unit);
}

function showView(name) {
  $$('[data-stl-fit-view]', ui.modal).forEach((v) => {
    v.hidden = v.getAttribute('data-stl-fit-view') !== name;
  });
  /* each view starts at its top, so the summary and chips are never above the fold */
  const box = $('.stl-fit-box', ui.modal);
  if (box) box.scrollTop = 0;
}

function fillForm(me) {
  ['bust', 'waist', 'hip', 'height'].forEach((name) => {
    writeField(ui.form.elements[name], me ? Number(me[name]) : NaN);
  });
}

function fieldError(name, text) {
  const input = ui.form.elements[name];
  const box = $('[data-stl-fit-err="' + name + '"]', ui.form);
  if (box) box.textContent = text || '';
  if (text) input.setAttribute('aria-invalid', 'true');
  else input.removeAttribute('aria-invalid');
}

/**
 * The form-wide line is a polite live region that stays in the accessibility tree (never
 * display:none), and it is cleared first and written a frame later so the same words are
 * announced again on a second failed press (29 Oct 2026 review, A1).
 */
function say(text) {
  const msg = $('[data-stl-fit-formmsg]', ui.form);
  if (!msg) return;
  msg.textContent = '';
  if (text) window.requestAnimationFrame(() => {
    msg.textContent = text;
  });
}

/** Reads and checks the form against the engine's bounds. Returns { cm values } or null. */
function readForm(engine) {
  const w = ui.w;
  const me = { unit: ui.unit };
  const bad = [];
  /* no space before cm ("60 and 160cm"), the word for inches ("24 and 62.5 inches") */
  const unitWord = ui.unit === 'in' ? ' ' + (w.in_word || 'inches') : w.cm_word || 'cm';
  ['bust', 'waist', 'hip', 'height'].forEach((name) => {
    const input = ui.form.elements[name];
    fieldError(name, '');
    const kept = keptCm(input);
    let res;
    if (kept !== null) {
      res = engine.check(name, kept, 'cm');
    } else {
      const n = typedNumber(name, input.value, ui.unit);
      if (n === null) return;
      res = engine.check(name, Number.isFinite(n) ? n : 'x', ui.unit);
    }
    if (res.empty) return;
    if (!res.ok) {
      const low = engine.check(name, '', ui.unit);
      fieldError(name, fill(w.err_range, { low: low.low, high: low.high, unit: unitWord }));
      bad.push(name);
      return;
    }
    me[name] = res.cm;
  });
  if (bad.length) {
    say(fill(w.err_check, { list: joinList(bad.map((m) => w[m] || m), w.and_word || 'and') }));
    const first = ui.form.elements[bad[0]];
    if (document.activeElement === first) first.blur();
    first.focus();
    return null;
  }
  if (!me.bust && !me.waist && !me.hip) {
    say(w.err_none || '');
    ui.form.elements.bust.focus();
    return null;
  }
  say('');
  return me;
}

function joinList(items, or) {
  if (items.length < 2) return items.join('');
  return items.slice(0, -1).join(', ') + ' ' + or + ' ' + items[items.length - 1];
}

/** The colourway to show: in stock in her size first, then pre-order, then any colour on sale. */
function pickColour(style, size) {
  const list = style.colourways || [];
  const sizeOf = (c) => (size ? (c.sizes || []).find((z) => z.value === size) : null);
  const score = (c) => {
    const z = sizeOf(c);
    if (z && z.available && !z.pre) return 0;
    if (z && z.available) return 1;
    if (!size && c.available) return 0;
    if (c.available) return 2;
    return 3;
  };
  let best = null;
  list.forEach((c) => {
    if (!best || score(c) < score(best)) best = c;
  });
  return best ? { colour: best, variant: sizeOf(best) } : null;
}

function imageAt(url, width) {
  if (!url) return '';
  const u = String(url);
  if (/[?&]width=\d+/.test(u)) return u.replace(/([?&]width=)\d+/, '$1' + width);
  return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
}

function reasonFor(r, w) {
  const name = (m) => w[m] || m;
  const value = shown(r.value, ui.unit);
  /* "Bust 86cm", "Hips 104cm": the label and her number, so every reason reads as one thing.
     [word] is the in-a-sentence word ("hips") for lines that put it mid-sentence. */
  const said = (w[r.driver + '_label'] || name(r.driver)) + ' ' + value;
  const base = { measure: said, word: name(r.driver), size: r.band, value };
  /* one measurement alone needs the bigger size (the hips a size up from the waist): say so */
  const atBand = r.picks.filter((p) => p.size === r.band).length;
  if (r.picks.length > 1 && atBand === 1 && r.how === 'in') {
    return fill(w.decides, base);
  }
  if (r.how === 'below') return fill(w.below, { measure: name(r.driver), value: shown(r.smallest.by[r.driver], ui.unit) });
  if (r.how === 'shared' || r.how === 'gap') {
    const t = Object.assign({}, base, { a: r.pair[0], b: r.pair[1] });
    if (r.rule === 'larger') return fill(w.larger, t);
    if (r.rule === 'smaller') return fill(w.smaller, t);
    /* right on the line (85 ends XS and starts S) or exactly halfway in a gap: not "closer" to either */
    if (r.how === 'shared' || r.tie) return fill(w.on_line || w.nearest, t);
    return fill(w.nearest, t);
  }
  return fill(w.in_band, base);
}

function modelLine(style, me, w) {
  if (!style.model || !me.height) return '';
  const diff = me.height - style.model.height_cm;
  const tokens = { model_height: shownHeight(style.model.height_cm, ui.unit), model_size: style.model.size };
  if (diff <= -4) return fill(w.model_longer, tokens);
  if (diff >= 4) return fill(w.model_shorter, tokens);
  return fill(w.model_same, tokens);
}

/**
 * The fit note under a size. A colourway's own custom.fit_note (set in admin) wins over the
 * engine's draft note, the shown colour's first, then any colour of the style. A style with an
 * offset (the Peggy Shirt) keeps the engine's computed line, which says which size it moved from.
 */
function noteFor(style, r, data, colour) {
  const engineNote = (r.notes && r.notes[0]) || '';
  if (style.offset) return engineNote;
  const own = (data && data.notes) || {};
  const mine = colour && own[colour.handle];
  if (mine) return mine;
  const any = Object.keys(own).map((h) => own[h]).find((n) => n);
  return any || engineNote;
}

/** One card's data and its sort group: 0 in stock in her size, 1 pre-order, 2 sold out in it, 3 needs a measurement, 4 past our largest. */
function buildCard(engine, key, data, me) {
  const w = ui.w;
  const style = engine.TABLE[key];
  const r = engine.fit(key, me);
  const size = r.status === 'fit' ? r.size : null;
  const pick = pickColour(data, size);
  if (!pick) return null;
  const c = pick.colour;
  const v = pick.variant;
  let group = 3;
  let stock = '';
  let out = false;
  if (r.status === 'fit') {
    if (v && v.available && !v.pre) {
      group = 0;
      stock = fill(v.q <= 5 ? w.stock_low : w.stock_in, { size });
    } else if (v && v.available) {
      group = 1;
      stock = fill(w.stock_pre, { size });
    } else {
      group = 2;
      out = true;
      stock = fill(w.stock_out, { size });
    }
  } else if (r.status === 'above' || r.status === 'below') {
    group = 4;
  }
  const lines = [];
  if (r.status === 'fit') {
    lines.push(['stl-fit-size', fill(w.size, { size })]);
    lines.push(['stl-fit-why', reasonFor(r, w)]);
    const note = noteFor(style, r, data, c);
    if (r.offsetApplied) {
      /* the Peggy Shirt: the size-down line comes straight after the reason that names the band,
         so "Your size: XL" and "sits in our 2XL" are explained before any estimate line, and the
         estimate names both sizes (29 Oct 2026 review, F5) */
      if (note) lines.push(['stl-fit-note', note]);
      if (r.estimated) lines.push(['stl-fit-why', fill(w.estimated_down || w.estimated, { band: r.band, size })]);
    } else {
      if (r.estimated) lines.push(['stl-fit-why', fill(w.estimated, { size: r.band })]);
      if (note) lines.push(['stl-fit-note', note]);
    }
  } else if (r.status === 'above') {
    const m = r.above.measure;
    /* when our largest band is the engine's own estimate, quote the printed top instead (F6) */
    const p = r.printed;
    if (p && p.size === r.largest.size) {
      /* a combined band printed only part of the way (Flora Wrap Skirt XL - 2XL to hips 115cm) */
      lines.push(['stl-fit-why', fill(w.above, { size: p.size, measure: w[m] || m, value: shown(p.value, ui.unit) })]);
    } else if (p && w.above_est) {
      lines.push(['stl-fit-why', fill(w.above_est, { printed: p.size, size: r.largest.size, measure: w[m] || m, value: shown(p.value, ui.unit) })]);
    } else {
      lines.push(['stl-fit-why', fill(w.above, { size: r.largest.size, measure: w[m] || m, value: shown(r.largest.by[m], ui.unit) })]);
    }
  } else if (r.status === 'below') {
    const m = r.below.measure;
    lines.push(['stl-fit-why', fill(w.below_none, { size: r.smallest.size, measure: w[m] || m, value: shown(r.smallest.by[m], ui.unit) })]);
  } else {
    const missing = style.drivers.filter((d) => !me[d]).map((d) => w[d] || d);
    lines.push(['stl-fit-why', fill(w.need, { list: joinList(missing, w.or || 'or') })]);
  }
  const price = v ? v.price : c.price;
  const compare = v ? v.compare_at : c.compare_at;
  return {
    key,
    group,
    category: style.category,
    name: data.name || style.name,
    colour: (data.colourways || []).length > 1 ? c.label : '',
    href: c.url + (v && r.status === 'fit' ? (c.url.indexOf('?') === -1 ? '?' : '&') + 'variant=' + v.id : ''),
    image: c.image,
    /* the name is text in the same link, so the photo is decorative here (A8) */
    alt: '',
    price: money(price),
    compare: compare ? money(compare) : '',
    lines,
    stock,
    out,
    len: r.status === 'fit' ? modelLine(style, me, w) : '',
    inStock: group === 0,
    /* buyable, but not in her size: folded under one quiet line at the end (F4, 29 Oct 2026) */
    folded: group === 2,
  };
}

function cardNode(card) {
  const li = el('li', 'stl-fit-card' + (card.group >= 2 ? ' stl-fit-card--quiet' : ''));
  li.setAttribute('data-stl-fit-cat', card.category);
  const a = el('a');
  a.href = card.href;
  const shot = el('span', 'stl-fit-shot');
  if (card.image) {
    const img = el('img');
    img.src = imageAt(card.image, 240);
    img.srcset = imageAt(card.image, 240) + ' 240w, ' + imageAt(card.image, 480) + ' 480w';
    img.sizes = '(min-width: 900px) 110px, 92px';
    img.width = 240;
    img.height = 320;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.alt = card.alt;
    shot.appendChild(img);
  }
  a.appendChild(shot);
  const info = el('span', 'stl-fit-info');
  const name = el('span', 'stl-fit-name', card.name);
  info.appendChild(name);
  if (card.colour) info.appendChild(el('span', 'stl-fit-colour', card.colour));
  const price = el('span', 'stl-fit-price', card.price);
  if (card.compare) {
    const was = el('s');
    if (ui.w.was_word) was.appendChild(el('span', 'visually-hidden', ui.w.was_word + ' '));
    was.appendChild(document.createTextNode(card.compare));
    price.appendChild(was);
  }
  info.appendChild(price);
  card.lines.forEach(([cls, text]) => {
    if (text) info.appendChild(el('span', cls, text));
  });
  if (card.stock) {
    const s = el('span', 'stl-fit-stock');
    s.appendChild(el('span', 'stl-dot' + (card.out ? ' stl-dot--out' : '')));
    s.appendChild(document.createTextNode(card.stock));
    info.appendChild(s);
  }
  if (card.len) info.appendChild(el('span', 'stl-fit-len', card.len));
  a.appendChild(info);
  li.appendChild(a);
  return li;
}

/**
 * The pieces she can buy in her size are the list. The ones she can buy, just not in her size
 * right now, fold under one quiet line at the end that opens on a tap, each card still linking to
 * its product page with her size picked, where the waitlist is (F4, decided for Chris 29 Oct 2026).
 */
function paintMore(folded) {
  const w = ui.w;
  const more = $('[data-stl-fit-more]', ui.modal);
  const box = $('[data-stl-fit-more-list]', ui.modal);
  if (!more || !box) return false;
  if (!folded.length) {
    more.hidden = true;
    box.hidden = true;
    box.replaceChildren();
    return true;
  }
  more.hidden = false;
  $('[data-stl-fit-more-text]', more).textContent = fill(w.more || 'Not in your size right now ([n])', { n: folded.length });
  more.setAttribute('aria-expanded', ui.moreOpen ? 'true' : 'false');
  box.replaceChildren(...folded.map(cardNode));
  box.hidden = !ui.moreOpen;
  return true;
}

function paintList() {
  const w = ui.w;
  const list = $('[data-stl-fit-list]', ui.modal);
  const status = $('[data-stl-fit-status]', ui.modal);
  const chipCards = ui.cards.filter((c) => ui.chip === 'all' || c.category === ui.chip);
  const folded = chipCards.filter((c) => c.folded);
  /* no fold markup (an old section file): every card stays in the one list, as before */
  const canFold = paintMore(folded);
  const shownCards = canFold ? chipCards.filter((c) => !c.folded) : chipCards;
  list.replaceChildren(...shownCards.map(cardNode));
  const n = shownCards.filter((c) => c.inStock).length;
  if (!shownCards.length && !(canFold && folded.length)) status.textContent = w.chip_empty || '';
  else if (n === 1) status.textContent = w.count_one || fill(w.count, { n });
  else status.textContent = n ? fill(w.count, { n }) : w.count_none || '';
  $$('[data-stl-fit-chip]', ui.modal).forEach((b) => b.setAttribute('aria-pressed', b.getAttribute('data-stl-fit-chip') === ui.chip ? 'true' : 'false'));
}

async function showResults(me, focus) {
  const w = ui.w;
  showView('results');
  const parts = [];
  ['bust', 'waist', 'hip'].forEach((m) => {
    if (me[m]) parts.push((w[m] || m) + ' ' + shown(me[m], ui.unit));
  });
  if (me.height) parts.push((w.height || 'height') + ' ' + shownHeight(me.height, ui.unit));
  const you = $('[data-stl-fit-you]', ui.modal);
  you.textContent = fill(w.you, { list: parts.join(', ') });
  const status = $('[data-stl-fit-status]', ui.modal);
  const list = $('[data-stl-fit-list]', ui.modal);
  list.replaceChildren();
  status.textContent = w.loading || '';
  if (focus) you.focus({ preventScroll: true });
  try {
    const [engine, styles] = await load(ui.modal);
    const byKey = {};
    styles.forEach((s) => {
      byKey[s.key] = s;
    });
    const cards = [];
    Object.keys(engine.TABLE).forEach((key, order) => {
      let data = byKey[key];
      if (!data) {
        /* a style whose metaobject handle differs from the table key: match on a colourway handle */
        const handles = engine.TABLE[key].handles || [];
        data = styles.find((s) => (s.handles || []).some((h) => handles.indexOf(h) !== -1));
      }
      if (!data || !(data.colourways || []).length) return;
      /* nothing in any colour or size can be bought (no stock, not continue-selling or pre-order):
         no card at all, so she never gets one for something she cannot buy (F4) */
      const buyable = data.colourways.some((c) => c.available || (c.sizes || []).some((z) => z.available));
      if (!buyable) return;
      const card = buildCard(engine, key, data, me);
      if (card) {
        card.order = order;
        cards.push(card);
      }
    });
    cards.sort((a, b) => a.group - b.group || a.order - b.order);
    ui.cards = cards;
    ui.moreOpen = false;
    /* a frame after the box has opened, so the count is announced (A11) */
    await new Promise((done) => window.requestAnimationFrame(done));
    paintList();
  } catch (e) {
    status.textContent = w.error || '';
  }
}

function openBox() {
  const modal = ui.modal;
  if (!modal) return;
  const me = storage.get(KEY_ME, null);
  const hasMe = me && (me.bust || me.waist || me.hip);
  /* start both downloads now, so the answer is ready by the time she presses the button */
  load(modal).catch(() => {});
  if (hasMe) {
    ui.me = me;
    showResults(me, false);
    /* her saved measurements are read first, not a bare "Edit" (A11) */
    openOverlay(MODAL, { focus: '[data-stl-fit-you]', returnTo: tab.open });
  } else {
    showView('form');
    fillForm(null);
    openOverlay(MODAL, { focus: '#stl-fit-bust', returnTo: tab.open });
  }
}

function wireBox() {
  const modal = document.getElementById(MODAL);
  if (!modal) return;
  ui.modal = modal;
  ui.form = $('[data-stl-fit-form]', modal);
  ui.w = words();
  const saved = storage.get(KEY_UNIT, null);
  setUnit(saved || modal.getAttribute('data-stl-fit-unit') || 'cm', false);

  modal.addEventListener('click', (event) => {
    if (event.target === modal) closeOverlay(MODAL);
  });

  $$('[data-stl-fit-unit-btn]', modal).forEach((b) => {
    b.addEventListener('click', () => setUnit(b.getAttribute('data-stl-fit-unit-btn'), true));
  });

  ui.form.addEventListener('input', (event) => {
    const t = event.target;
    if (t && t.name) fieldError(t.name, '');
  });

  ui.form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const go = $('button[type="submit"]', ui.form);
    go.setAttribute('aria-busy', 'true');
    let engine;
    try {
      [engine] = await load(modal);
    } catch (e) {
      go.removeAttribute('aria-busy');
      $('[data-stl-fit-formmsg]', ui.form).textContent = ui.w.error || '';
      return;
    }
    go.removeAttribute('aria-busy');
    const me = readForm(engine);
    if (!me) return;
    ui.me = me;
    storage.set(KEY_ME, me);
    ui.chip = 'all';
    showResults(me, true);
  });

  $('[data-stl-fit-edit]', modal).addEventListener('click', () => {
    showView('form');
    fillForm(ui.me || storage.get(KEY_ME, null));
    const first = ui.form.elements.bust;
    window.requestAnimationFrame(() => first.focus({ preventScroll: true }));
  });

  const more = $('[data-stl-fit-more]', modal);
  if (more) {
    more.addEventListener('click', () => {
      ui.moreOpen = !ui.moreOpen;
      more.setAttribute('aria-expanded', ui.moreOpen ? 'true' : 'false');
      const box = $('[data-stl-fit-more-list]', modal);
      if (box) box.hidden = !ui.moreOpen;
    });
  }

  $$('[data-stl-fit-chip]', modal).forEach((b) => {
    b.addEventListener('click', () => {
      const chip = b.getAttribute('data-stl-fit-chip');
      ui.chip = chip === 'all' || CATS.indexOf(chip) !== -1 ? chip : 'all';
      paintList();
    });
  });
}

/* ------------------------------------------------------------------ wiring */

/** Once the tab is put away its focus has nowhere to be: hand it to the $15 tab or the Questions? pill (A9). */
function focusAfterDismiss() {
  window.requestAnimationFrame(() => {
    const teaser = document.getElementById('stl-teaser');
    const next =
      (teaser && teaser.classList.contains(ON) && $('[data-stl-teaser-open]', teaser)) ||
      ((document.getElementById('stl-askPill') || {}).getClientRects && document.getElementById('stl-askPill').getClientRects().length && document.getElementById('stl-askPill')) ||
      null;
    if (next && next.focus) next.focus({ preventScroll: true });
  });
}

onReady(() => {
  tab.el = document.getElementById('stl-fitTab');
  if (!tab.el) return;
  tab.open = $('[data-stl-fit-open]', tab.el);
  tab.off = storage.get(KEY_OFF, false) === true;
  wireBox();

  tab.open.addEventListener('click', () => {
    tab.opened = true;
    openBox();
    tabSync();
  });

  /* the tab's X (900px and up) and the quiet link inside the box (phones) both put it away for good */
  const dismiss = (fromBox) => {
    tab.off = true;
    tab.opened = false;
    storage.set(KEY_OFF, true);
    if (fromBox) closeOverlay(MODAL);
    tabSync();
    focusAfterDismiss();
  };
  $('[data-stl-fit-dismiss]', tab.el).addEventListener('click', () => dismiss(false));
  const hide = $('[data-stl-fit-hide]', ui.modal || document);
  if (hide) hide.addEventListener('click', () => dismiss(true));

  document.addEventListener('stl:overlay:change', (event) => {
    const detail = event.detail || {};
    tabSync();
    /* the tab hides while the box is open, so core's focus return finds nothing to land on:
       put focus back on the tab once it is showing again (as the $15 tab does) */
    if (detail.id === MODAL && detail.open === false && tab.opened) {
      tab.opened = false;
      window.requestAnimationFrame(() => {
        if (tab.el.classList.contains(ON)) tab.open.focus({ preventScroll: true });
      });
    }
  });

  /* the $15 tab coming and going moves this one; the Questions? panel docking hides it */
  const teaser = document.getElementById('stl-teaser');
  if (teaser && window.MutationObserver) {
    new MutationObserver(() => {
      /* the $15 tab coming or going changes the room beside it: re-place a showing tab */
      const was = tab.el.classList.contains(ON);
      tabSync();
      if (was && tab.el.classList.contains(ON)) place();
    }).observe(teaser, { attributes: true, attributeFilter: ['class'] });
  }
  if (window.MutationObserver) {
    new MutationObserver(tabSync).observe(document.documentElement, { attributes: true, attributeFilter: ['data-stl-ask-docked', 'data-stl-busy'] });
  }
  let resizeFrame = 0;
  window.addEventListener('resize', () => {
    window.cancelAnimationFrame(resizeFrame);
    resizeFrame = window.requestAnimationFrame(() => {
      if (tab.el.classList.contains(ON)) place();
    });
  });

  const delay = (Number(tab.el.getAttribute('data-stl-fit-delay')) || 5) * 1000;
  if (!tab.off) startClock(delay);
});
